// Skill Mapping Service
// Maps natural language inputs to structured skill categories

export interface SkillMapping {
    input: string;
    detectedSkills: {
        name: string;
        category: 'professional' | 'practical' | 'soft' | 'physical';
        confidence: number;
    }[];
    suggestedCategory: 'professional' | 'practical' | 'soft' | 'physical';
}

// Comprehensive skill mapping database
const SKILL_MAP: Record<string, SkillMapping> = {
    // Professional/Technical Skills
    'coding': {
        input: 'coding',
        detectedSkills: [
            { name: 'Software Development', category: 'professional', confidence: 0.95 },
            { name: 'Programming', category: 'professional', confidence: 0.9 },
            { name: 'Problem Solving', category: 'soft', confidence: 0.7 }
        ],
        suggestedCategory: 'professional'
    },
    'programming': {
        input: 'programming',
        detectedSkills: [
            { name: 'Software Development', category: 'professional', confidence: 0.95 },
            { name: 'Coding', category: 'professional', confidence: 0.9 }
        ],
        suggestedCategory: 'professional'
    },
    'design': {
        input: 'design',
        detectedSkills: [
            { name: 'Graphic Design', category: 'professional', confidence: 0.85 },
            { name: 'UI/UX Design', category: 'professional', confidence: 0.8 },
            { name: 'Creativity', category: 'soft', confidence: 0.75 }
        ],
        suggestedCategory: 'professional'
    },
    'web development': {
        input: 'web development',
        detectedSkills: [
            { name: 'Frontend Development', category: 'professional', confidence: 0.9 },
            { name: 'Backend Development', category: 'professional', confidence: 0.85 },
            { name: 'Web Design', category: 'professional', confidence: 0.8 }
        ],
        suggestedCategory: 'professional'
    },

    // Practical/Handy Skills
    'building': {
        input: 'building',
        detectedSkills: [
            { name: 'Construction', category: 'practical', confidence: 0.9 },
            { name: 'Carpentry', category: 'practical', confidence: 0.85 },
            { name: 'Manual Labor', category: 'physical', confidence: 0.8 }
        ],
        suggestedCategory: 'practical'
    },
    'construction': {
        input: 'construction',
        detectedSkills: [
            { name: 'Construction Helper', category: 'practical', confidence: 0.95 },
            { name: 'Building', category: 'practical', confidence: 0.9 },
            { name: 'Physical Endurance', category: 'physical', confidence: 0.75 }
        ],
        suggestedCategory: 'practical'
    },
    'driving': {
        input: 'driving',
        detectedSkills: [
            { name: 'Personal Driver', category: 'practical', confidence: 0.95 },
            { name: 'Transport', category: 'practical', confidence: 0.9 },
            { name: 'Navigation', category: 'soft', confidence: 0.7 }
        ],
        suggestedCategory: 'practical'
    },
    'gardening': {
        input: 'gardening',
        detectedSkills: [
            { name: 'Landscaping', category: 'practical', confidence: 0.9 },
            { name: 'Plant Care', category: 'practical', confidence: 0.85 },
            { name: 'Outdoor Work', category: 'physical', confidence: 0.8 }
        ],
        suggestedCategory: 'practical'
    },
    'cleaning': {
        input: 'cleaning',
        detectedSkills: [
            { name: 'Housekeeping', category: 'practical', confidence: 0.95 },
            { name: 'Sanitation', category: 'practical', confidence: 0.85 },
            { name: 'Attention to Detail', category: 'soft', confidence: 0.7 }
        ],
        suggestedCategory: 'practical'
    },
    'cooking': {
        input: 'cooking',
        detectedSkills: [
            { name: 'Culinary Arts', category: 'practical', confidence: 0.9 },
            { name: 'Food Preparation', category: 'practical', confidence: 0.85 },
            { name: 'Kitchen Management', category: 'soft', confidence: 0.7 }
        ],
        suggestedCategory: 'practical'
    },
    'repair': {
        input: 'repair',
        detectedSkills: [
            { name: 'Maintenance', category: 'practical', confidence: 0.9 },
            { name: 'Troubleshooting', category: 'practical', confidence: 0.85 },
            { name: 'Problem Solving', category: 'soft', confidence: 0.75 }
        ],
        suggestedCategory: 'practical'
    },
    'plumbing': {
        input: 'plumbing',
        detectedSkills: [
            { name: 'Plumbing Services', category: 'practical', confidence: 0.95 },
            { name: 'Pipe Fitting', category: 'practical', confidence: 0.9 },
            { name: 'Tool Operation', category: 'practical', confidence: 0.8 }
        ],
        suggestedCategory: 'practical'
    },
    'electrical': {
        input: 'electrical',
        detectedSkills: [
            { name: 'Electrical Work', category: 'practical', confidence: 0.95 },
            { name: 'Wiring', category: 'practical', confidence: 0.9 },
            { name: 'Safety Awareness', category: 'soft', confidence: 0.75 }
        ],
        suggestedCategory: 'practical'
    },

    // Soft Skills
    'communication': {
        input: 'communication',
        detectedSkills: [
            { name: 'Verbal Communication', category: 'soft', confidence: 0.95 },
            { name: 'Written Communication', category: 'soft', confidence: 0.9 },
            { name: 'Interpersonal Skills', category: 'soft', confidence: 0.85 }
        ],
        suggestedCategory: 'soft'
    },
    'leadership': {
        input: 'leadership',
        detectedSkills: [
            { name: 'Team Leadership', category: 'soft', confidence: 0.95 },
            { name: 'Management', category: 'soft', confidence: 0.9 },
            { name: 'Decision Making', category: 'soft', confidence: 0.85 }
        ],
        suggestedCategory: 'soft'
    },
    'teamwork': {
        input: 'teamwork',
        detectedSkills: [
            { name: 'Collaboration', category: 'soft', confidence: 0.95 },
            { name: 'Team Communication', category: 'soft', confidence: 0.9 },
            { name: 'Cooperation', category: 'soft', confidence: 0.85 }
        ],
        suggestedCategory: 'soft'
    },
    'organization': {
        input: 'organization',
        detectedSkills: [
            { name: 'Time Management', category: 'soft', confidence: 0.9 },
            { name: 'Planning', category: 'soft', confidence: 0.85 },
            { name: 'Project Management', category: 'professional', confidence: 0.75 }
        ],
        suggestedCategory: 'soft'
    },

    // Physical Skills
    'lifting': {
        input: 'lifting',
        detectedSkills: [
            { name: 'Heavy Lifting', category: 'physical', confidence: 0.95 },
            { name: 'Physical Strength', category: 'physical', confidence: 0.9 },
            { name: 'Endurance', category: 'physical', confidence: 0.8 }
        ],
        suggestedCategory: 'physical'
    },
    'physical work': {
        input: 'physical work',
        detectedSkills: [
            { name: 'Manual Labor', category: 'physical', confidence: 0.95 },
            { name: 'Physical Endurance', category: 'physical', confidence: 0.9 },
            { name: 'Stamina', category: 'physical', confidence: 0.85 }
        ],
        suggestedCategory: 'physical'
    }
};

/**
 * Map user's natural language input to skill suggestions
 */
export function mapInputToSkills(input: string): SkillMapping {
    const normalized = input.toLowerCase().trim();

    // Direct match
    if (SKILL_MAP[normalized]) {
        return SKILL_MAP[normalized];
    }

    // Partial match
    for (const [key, mapping] of Object.entries(SKILL_MAP)) {
        if (normalized.includes(key) || key.includes(normalized)) {
            return mapping;
        }
    }

    // Default - treat as generic skill
    return {
        input,
        detectedSkills: [
            { name: input, category: 'professional', confidence: 0.5 }
        ],
        suggestedCategory: 'professional'
    };
}

/**
 * Get skill categories for display
 */
export function getSkillCategories() {
    return [
        {
            id: 'professional',
            name: 'Professional Skills',
            icon: 'FiBriefcase',
            description: 'Tech, design, business, engineering',
            color: '#D4AF37'
        },
        {
            id: 'practical',
            name: 'Practical / Handy Skills',
            icon: 'FiTool',
            description: 'Construction, driving, cooking, repairs',
            color: '#4A90E2'
        },
        {
            id: 'physical',
            name: 'Physical Work',
            icon: 'FiActivity',
            description: 'Lifting, endurance, outdoor work',
            color: '#7ED321'
        },
        {
            id: 'soft',
            name: 'People & Communication',
            icon: 'FiUsers',
            description: 'Leadership, teamwork, reliability',
            color: '#9013FE'
        }
    ];
}

/**
 * Get common work activities for each category
 */
export function getCommonActivities(category: string): string[] {
    const activities: Record<string, string[]> = {
        professional: [
            'Web Development', 'Mobile Apps', 'Graphic Design',
            'Data Analysis', 'Marketing', 'Project Management',
            'Accounting', 'Writing', 'Photography'
        ],
        practical: [
            'Construction', 'Driving', 'Cooking', 'Cleaning',
            'Gardening', 'Plumbing', 'Electrical Work', 'Painting',
            'Carpentry', 'Repairs'
        ],
        physical: [
            'Heavy Lifting', 'Loading/Unloading', 'Warehouse Work',
            'Delivery', 'Farm Work', 'Security', 'Outdoor Labor'
        ],
        soft: [
            'Team Leadership', 'Communication', 'Training',
            'Customer Service', 'Problem Solving', 'Time Management',
            'Conflict Resolution', 'Mentoring'
        ]
    };

    return activities[category] || [];
}

export default {
    mapInputToSkills,
    getSkillCategories,
    getCommonActivities
};
